import React from 'react';
import { getPaginationModel } from './paginationUtils';

const getOperationIcon = (operation) => {
  switch (operation) {
    case 'AND': return 'add_circle';
    case 'OR': return 'call_split'; // or 'layers', 'dns'
    case 'NOT': return 'remove_circle'; // or 'block'
    default: return 'help';
  }
};

const getOperationColor = (operation) => {
  switch (operation) {
    case 'AND': return '#4CAF50';
    case 'OR': return '#2196F3';
    case 'NOT': return '#f44336';
    default: return '#ccc';
  }
};

const CategoryManager = ({ categories, allCategories, onEdit, onDelete, searchTerm, onSearchChange, page, onPageChange, selectedCategoryIds, onCategorySelection, unselectAllCategories, searchInputRef }) => {
  const sortedCategories = [...categories].sort((a, b) => a.title.localeCompare(b.title));
  const pageSize = 5;
  const totalPages = Math.ceil(sortedCategories.length / pageSize);
  const paginatedCategories = sortedCategories.slice((page - 1) * pageSize, page * pageSize);
  const paginationModel = getPaginationModel(page, totalPages);

  const emptyRows = pageSize - paginatedCategories.length;

  return (
    <div>
      {/* Show controls if there are selected items OR if there are list items OR if searching */}
      {(categories.length > 0 || searchTerm.length > 0 || selectedCategoryIds.length > 0) && (
        <>
          <div className="recipe-filter-controls" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              {selectedCategoryIds.length > 0 && <button title="Unselect all categories" onClick={unselectAllCategories} className="unselect-button">Unselect</button>}
              <div className={`search-input-container ${selectedCategoryIds.length > 0 ? 'collapsed' : 'expanded'}`}>
                <input
                  ref={searchInputRef}
                  data-tour="categories-search"
                  type="text"
                  placeholder="Search Categories"
                  value={searchTerm}
                  onChange={e => onSearchChange(e.target.value)}
                />
                {searchTerm && <button onClick={() => { onSearchChange(''); searchInputRef.current.focus(); }} className="clear-input-button" title="Clear search"><i className="material-icons">close</i></button>}
              </div>
            </div>
            {selectedCategoryIds.length > 0 && (
              <div className="selected-items">
                {selectedCategoryIds.map(filter => {
                  const actualList = allCategories || categories;
                  const category = actualList.find(c => c.id === filter.id);
                  if (!category) return null;
                  return (
                    <div key={filter.id} className="selected-item" style={{ backgroundColor: getOperationColor(filter.operation) }}>
                      <span style={{ fontWeight: 'bold', marginRight: '5px' }}>{filter.operation}:</span>
                      {category.title}
                      <button onClick={() => onCategorySelection(filter.id, filter.operation)} className="remove-item-button"><i className="material-icons">close</i></button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
      {categories.length === 0 && searchTerm.length === 0 && selectedCategoryIds.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '1rem' }}>No categories found.<br />Add one by using the + button.</div>
      ) : null}
      {paginatedCategories.length === 0 && searchTerm.length > 0 ? (
        <div style={{ textAlign: 'center', padding: '1rem' }}>No categories found for "{searchTerm}".</div>
      ) : (
        <>
          <ul className="cookbook-list" data-tour="categories-list">
            {paginatedCategories.map((category) => {
              const activeFilter = selectedCategoryIds.find(f => f.id === category.id);
              const isSelected = !!activeFilter;

              return (
                <li key={category.id} data-tour={category.isTemporary ? `category-${category.id}` : null} className={isSelected ? 'selected' : ''} style={{ cursor: 'default' }}>
                  <span onClick={() => onCategorySelection(category.id)} style={{ flexGrow: 1, cursor: 'pointer' }}>{category.title}</span>

                  <div className="item-actions" style={{ marginRight: '10px' }}>
                    {/* AND Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); onCategorySelection(category.id, 'AND'); }}
                      className="icon-button"
                      title="Include (AND)"
                      style={{ color: (activeFilter?.operation === 'AND') ? '#4CAF50' : '#555' }}
                    >
                      <i className="material-icons">add_circle</i>
                    </button>

                    {/* OR Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); onCategorySelection(category.id, 'OR'); }}
                      className="icon-button"
                      title="Include (OR)"
                      style={{ color: (activeFilter?.operation === 'OR') ? '#2196F3' : '#555' }}
                    >
                      <i className="material-icons">call_split</i>
                    </button>

                    {/* NOT Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); onCategorySelection(category.id, 'NOT'); }}
                      className="icon-button"
                      title="Exclude (NOT)"
                      style={{ color: (activeFilter?.operation === 'NOT') ? '#f44336' : '#555' }}
                    >
                      <i className="material-icons">remove_circle</i>
                    </button>
                  </div>

                  <div className="item-actions">
                    <button onClick={(e) => { e.stopPropagation(); onEdit(category); }} className="icon-button" title="Edit category"><i className="material-icons">edit</i></button>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(category.id); }} className="icon-button delete-button" title="Delete category"><i className="material-icons">delete</i></button>
                  </div>
                </li>
              );
            })}
            {page === totalPages && totalPages > 1 && emptyRows > 0 && [...Array(emptyRows)].map((_, index) => (
              <li key={`empty-${index}`} style={{ visibility: 'hidden' }}>
                &nbsp;
                <div className="item-actions empty-row-actions">
                  <button className="icon-button" title="Edit category"><i className="material-icons">edit</i></button>
                  <button className="icon-button delete-button" title="Delete category"><i className="material-icons">delete</i></button>
                </div>
              </li>
            ))}
          </ul>
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button onClick={() => onPageChange(page - 1)} disabled={page === 1} className="icon-button" title="Previous page"><i className="material-icons">keyboard_arrow_left</i></button>
              <div className="page-numbers">
                {paginationModel.map((item, index) => (
                  <button key={index} onClick={() => onPageChange(item)} disabled={item === '...' || item === page} className={item === page ? 'active' : ''} title={`Go to page ${item}`}>
                    {item}
                  </button>
                ))}
              </div>
              <button onClick={() => onPageChange(page + 1)} disabled={paginatedCategories.length < pageSize || page * pageSize >= categories.length} className="icon-button" title="Next page"><i className="material-icons">keyboard_arrow_right</i></button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CategoryManager;